import { Fiche, type Fiche as FicheType } from '@pharmafiche/schema';

/**
 * The antiplatelet sample fiche.
 *
 * This is the canonical reference content for every template renderer.
 * It must be a *byte-for-byte* match to the bullets in
 * `docs/prototype/fiche-prototype.html`, so visual regressions can be diffed
 * against the prototype.
 */
const antiplateletFiche: FicheType = {
  schema_version: 1,
  id: 'sample-antiplatelet',
  meta: {
    title: 'LES ANTIAGRÉGANTS PLAQUETTAIRES',
    subtitle: undefined,
    tags: ['cardiologie', 'hémostase', 'P2Y12', 'AAP'],
    template_id: 'dessinee',
    language: 'fr',
    created_at: '2026-05-14T00:00:00.000Z',
    updated_at: '2026-05-14T00:00:00.000Z',
  },
  sections: [
    // 1. Introduction (blue, lean-l)
    {
      type: 'intro',
      id: 'sec-intro',
      title: 'Introduction',
      icon: '📖',
      color: 'blue',
      bullets: [
        {
          id: 'b-i1',
          text: 'Rôle central dans la **prévention** et le traitement des **thromboses artérielles**.',
        },
        {
          id: 'b-i2',
          text: "Indications majeures : **SCA**, angioplastie avec stent, prévention secondaire de l'**AVC ischémique**.",
        },
        { id: 'b-i3', text: "Ciblent différentes **voies d'activation plaquettaire**." },
      ],
      warning: 'Risque majeur commun : **hémorragie**',
    },
    // 2. Classification (green, lean-r)
    {
      type: 'classification',
      id: 'sec-classification',
      title: 'Classification des AAP',
      icon: '🗒️',
      color: 'green',
      bullets: [
        { id: 'b-c1', text: '**Inhibiteurs de la COX-1** : aspirine.', emphasis: 'blue' },
        {
          id: 'b-c2',
          text: '**Inhibiteurs de la voie ADP / P2Y₁₂** : clopidogrel, prasugrel, ticlopidine, ticagrelor.',
          emphasis: 'green',
        },
        { id: 'b-c3', text: '**Dipyridamole.**', emphasis: 'purple' },
        {
          id: 'b-c4',
          text: '**Anti-GPIIb/IIIa** : abciximab, eptifibatide, tirofiban.',
          emphasis: 'red',
        },
      ],
      note: 'AAP = hémostase primaire / plaquettes',
    },

    // 3. Pathway diagram
    {
      type: 'diagram',
      id: 'sec-pathway',
      diagram_id: 'antiplatelet-pathway',
    },

    // 4. Aspirine (blue)
    {
      type: 'drug_card',
      id: 'sec-aspirine',
      drug: {
        id: 'drug-aspirine',
        name: 'Aspirine',
        color: 'blue',
        icon: '💊',
        intro_bullets: [
          { id: 'b-asp1', text: '**Chef de file** : acide acétylsalicylique.' },
          {
            id: 'b-asp2',
            text: '**Mécanisme** : inhibition **irréversible** de la COX-1 plaquettaire → ↓ TXA₂.',
          },
          { id: 'b-asp3', text: '**Effet** : 7 à 10 j ; arrêt 5 à 7 j avant chirurgie.' },
        ],
        sub_sections: [
          {
            id: 'mh-asp-ind',
            icon: '🎯',
            label: 'Indications',
            bullets: [
              { id: 'b-asp-i1', text: 'Prévention secondaire IDM, AVC, revascularisation.' },
              { id: 'b-asp-i2', text: 'Prévention primaire désormais limitée.' },
            ],
          },
          {
            id: 'mh-asp-pos',
            icon: '📋',
            label: 'Posologie',
            bullets: [{ id: 'b-asp-p1', text: '75 à 325 mg/j per os.' }],
          },
          {
            id: 'mh-asp-ei',
            icon: '⚠️',
            label: 'EI / CI / Interactions',
            color: 'red',
            bullets: [
              {
                id: 'b-asp-e1',
                text: '**EI** : hémorragie ++ ; effets digestifs dose-dépendants.',
              },
              {
                id: 'b-asp-e2',
                text: '**CI** : hypersensibilité, lésion saignante, UGD, grossesse (T3).',
              },
              {
                id: 'b-asp-e3',
                text: '**Interactions** : AAP, anticoagulants, AINS ; prudence IEC/ARA2.',
              },
            ],
          },
        ],
      },
    },

    // 5. Thiénopyridines (green)
    {
      type: 'drug_card',
      id: 'sec-thieno',
      drug: {
        id: 'drug-thieno',
        name: 'Thiénopyridines',
        color: 'green',
        icon: '💊',
        intro_bullets: [
          { id: 'b-th1', text: '**Molécules** : clopidogrel, prasugrel, ticlopidine.' },
          {
            id: 'b-th2',
            text: '**Mécanisme** : antagonisme **irréversible** du récepteur P2Y₁₂.',
          },
          { id: 'b-th3', text: "**Durée d'effet** > 5 jours." },
        ],
        sub_sections: [
          {
            id: 'mh-th-ind',
            icon: '🎯',
            label: 'Indications',
            bullets: [
              { id: 'b-th-i1', text: 'SCA ± sus-décalage ST.' },
              { id: 'b-th-i2', text: 'AVC ischémique athérothrombotique.' },
              { id: 'b-th-i3', text: 'Angioplastie coronaire ; AOMI.' },
            ],
          },
          {
            id: 'mh-th-part',
            icon: '⚙️',
            label: 'Particularités',
            bullets: [
              {
                id: 'b-th-p1',
                text: '**Clopidogrel** : prodrogue, activation hépatique (CYP2C19).',
              },
              { id: 'b-th-p2', text: '**Prasugrel** : prodrogue, métabolisme hépatique.' },
              { id: 'b-th-p3', text: "**Ticlopidine** : ancienne, plus d'EI." },
            ],
          },
          {
            id: 'mh-th-ei',
            icon: '⚠️',
            label: 'EI / CI / Interactions',
            color: 'red',
            bullets: [
              { id: 'b-th-e1', text: '**EI** : hémorragies++, dyspepsie ; rares cytopénies, PTT.' },
              { id: 'b-th-e2', text: '**CI** : hypersensibilité, saignement, IH sévère.' },
              {
                id: 'b-th-e3',
                text: '**Clé** : inhibiteurs CYP2C19 (oméprazole) + clopidogrel → à éviter.',
              },
            ],
          },
        ],
      },
    },

    // 6. Ticagrelor (purple)
    {
      type: 'drug_card',
      id: 'sec-ticagrelor',
      drug: {
        id: 'drug-ticagrelor',
        name: 'Ticagrelor',
        color: 'purple',
        icon: '💊',
        intro_bullets: [
          {
            id: 'b-tic1',
            text: 'Antagoniste oral **direct, réversible, non compétitif** du P2Y₁₂.',
          },
          { id: 'b-tic2', text: 'Actif **sans bioactivation** préalable.' },
          { id: 'b-tic3', text: 'Supériorité clinique vs clopidogrel dans le SCA.' },
        ],
        sub_sections: [
          {
            id: 'mh-tic-pos',
            icon: '📋',
            label: 'Posologie',
            bullets: [{ id: 'b-tic-p1', text: '90 mg × 2 / jour per os.' }],
          },
          {
            id: 'mh-tic-pk',
            icon: '⏱️',
            label: 'Pharmacocinétique',
            bullets: [
              { id: 'b-tic-pk1', text: 'Métabolisme CYP3A4.' },
              { id: 'b-tic-pk2', text: "Demi-vie 7 à 8,5 h ; durée d'effet ~ 96 h." },
            ],
          },
          {
            id: 'mh-tic-ei',
            icon: '⚠️',
            label: 'EI',
            color: 'red',
            bullets: [
              {
                id: 'b-tic-e1',
                text: 'Syndrome hémorragique, **dyspnée**, bradycardie, hyperuricémie, ↑ créat, paresthésies.',
              },
            ],
          },
          {
            id: 'mh-tic-ci',
            icon: '🛡️',
            label: 'CI / Précautions',
            color: 'red',
            bullets: [
              { id: 'b-tic-c1', text: 'ATCD hémorragie intracrânienne ; IH modérée à sévère.' },
              { id: 'b-tic-c2', text: 'Prudence si bradycardie, asthme, BPCO.' },
            ],
          },
          {
            id: 'mh-tic-int',
            icon: '🔗',
            label: 'Interactions',
            bullets: [{ id: 'b-tic-int1', text: 'Inducteurs / inhibiteurs du CYP3A4.' }],
          },
        ],
      },
    },

    // 7. Dipyridamole (orange)
    {
      type: 'drug_card',
      id: 'sec-dipyridamole',
      drug: {
        id: 'drug-dipy',
        name: 'Dipyridamole',
        color: 'orange',
        icon: '💊',
        intro_bullets: [
          {
            id: 'b-dipy1',
            text: 'Inhibition de la voie **ADP** et des **phosphodiestérases** → ↑ AMPc plaquettaire.',
          },
        ],
        sub_sections: [
          {
            id: 'mh-dipy-pos',
            icon: '📋',
            label: 'Posologie',
            bullets: [{ id: 'b-dipy-p1', text: '150 mg × 2 / jour per os.' }],
          },
          {
            id: 'mh-dipy-pk',
            icon: '⏱️',
            label: 'Pharmacocinétique',
            bullets: [
              { id: 'b-dipy-pk1', text: 'Biodisponibilité ~ 100 %.' },
              { id: 'b-dipy-pk2', text: "Demi-vie ~ 12 h ; durée d'effet < 24 h." },
            ],
          },
          {
            id: 'mh-dipy-ei',
            icon: '⚠️',
            label: 'EI',
            color: 'red',
            bullets: [
              {
                id: 'b-dipy-e1',
                text: 'Céphalées, bouffées de chaleur, hypotension, éruptions, troubles digestifs.',
              },
            ],
          },
        ],
      },
    },

    // 8. Anti-GPIIb/IIIa (red)
    {
      type: 'drug_card',
      id: 'sec-antigp',
      drug: {
        id: 'drug-antigp',
        name: 'Anti-GPIIb/IIIa',
        color: 'red',
        icon: '💊',
        intro_bullets: [
          { id: 'b-agp1', text: '**Molécules** : abciximab, eptifibatide, tirofiban.' },
          {
            id: 'b-agp2',
            text: '**Mécanisme** : antagonisme des récepteurs GPIIb/IIIa au fibrinogène.',
          },
        ],
        sub_sections: [
          {
            id: 'mh-agp-use',
            icon: '💉',
            label: "Usage d'urgence uniquement",
            bullets: [
              {
                id: 'b-agp-u1',
                text: 'IV en perfusion continue, souvent + héparine + aspirine + clopidogrel.',
              },
              { id: 'b-agp-u2', text: 'Effet rapidement **réversible** après arrêt.' },
            ],
          },
          {
            id: 'mh-agp-ind',
            icon: '🎯',
            label: 'Indication principale',
            bullets: [{ id: 'b-agp-i1', text: 'Angioplastie au cours des SCA.' }],
          },
          {
            id: 'mh-agp-ei',
            icon: '⚠️',
            label: 'EI / Précautions',
            color: 'red',
            bullets: [
              { id: 'b-agp-e1', text: '**EI** : risque hémorragique, thrombopénie.' },
              {
                id: 'b-agp-e2',
                text: '**Précautions** : adapter à la fonction rénale ; demi-dose tirofiban si DFG < 30 ml/min.',
              },
            ],
          },
        ],
      },
    },

    // 9. Associations & Surveillance (blue)
    {
      type: 'intro',
      id: 'sec-assoc',
      title: 'Associations & Surveillance',
      icon: '🔍',
      color: 'blue',
      bullets: [
        { id: 'b-as1', text: 'Association **aspirine + inhibiteur P2Y₁₂** fréquente.' },
        { id: 'b-as2', text: '**Objectif** : ↑ efficacité antithrombotique.' },
        { id: 'b-as3', text: '**Limite** : majoration du risque hémorragique.' },
        { id: 'b-as4', text: 'Pas de surveillance biologique de routine.' },
        {
          id: 'b-as5',
          text: '**Surveillance clinique** : gingivorragies, épistaxis, ecchymoses, méléna, hématurie.',
        },
      ],
    },

    // 10. Rôle du pharmacien (green)
    {
      type: 'intro',
      id: 'sec-pharma-role',
      title: 'Rôle du pharmacien',
      icon: '🧑‍⚕️',
      color: 'green',
      bullets: [
        { id: 'b-pr1', text: 'Sécurisation du traitement.' },
        { id: 'b-pr2', text: 'Évaluer le risque hémorragique.' },
        { id: 'b-pr3', text: 'Analyser posologie & interactions.' },
        { id: 'b-pr4', text: "Suivre l'observance, éducation thérapeutique." },
        { id: 'b-pr5', text: 'Pharmacovigilance.' },
      ],
      warning: '💡 Ne jamais arrêter seul ; éviter AINS ; signaler tout saignement.',
    },

    // 11. Cas clinique (red)
    {
      type: 'clinical_case',
      id: 'sec-case',
      patient: 'M. Ahmed, 62 ans, post-SCA + stent.',
      prescription: [
        '**Ordonnance** : aspirine 100 mg/j ; clopidogrel 75 mg/j ; atorvastatine 40 mg/j ; bisoprolol 5 mg/j.',
      ],
      question: 'gingivorragie au brossage.',
      conduite: [
        { id: 'b-case-c1', text: 'Ne pas arrêter la bithérapie sans avis médical.' },
        { id: 'b-case-c2', text: 'Vérifier autres signes de saignement.' },
        { id: 'b-case-c3', text: 'Brosse à dents souple + brossage doux.' },
        { id: 'b-case-c4', text: "Adresser si saignement important ou signes d'alarme." },
      ],
    },

    // 12. Mnemonic
    {
      type: 'mnemonic',
      id: 'sec-mnemo',
      text: '**MNÉMO :** AAP = plaquettes = thromboses artérielles',
    },

    // 13. Final warning
    {
      type: 'warning',
      id: 'sec-final-warn',
      text: 'Tous les antiagrégants plaquettaires exposent à un risque hémorragique : balance bénéfice/risque, surveillance clinique et éducation du patient.',
      icon: '⚠️',
    },
  ],
  diagrams: [
    {
      id: 'antiplatelet-pathway',
      title: "Voies d'activation plaquettaire",
      nodes: [
        // Top row — drug interventions
        {
          id: 'n-thieno',
          type: 'drug',
          color: 'green',
          label: 'Thiénopyridines',
          sublabel: 'Blocage P2Y₁₂',
          position: { x: 20, y: 0 },
        },
        {
          id: 'n-aspi',
          type: 'drug',
          color: 'blue',
          label: 'Aspirine',
          sublabel: 'Inhibition COX-1',
          position: { x: 340, y: 0 },
        },
        {
          id: 'n-antigp',
          type: 'drug',
          color: 'red',
          label: 'Anti-GPIIb/IIIa',
          sublabel: 'Blocage fibrinogène',
          position: { x: 660, y: 0 },
        },
        // Main flow — left to right
        {
          id: 'n-p2y12',
          type: 'receptor',
          color: 'green',
          label: 'P2Y₁₂',
          sublabel: 'récepteur',
          position: { x: 60, y: 160 },
        },
        {
          id: 'n-platelet',
          type: 'process',
          color: 'yellow',
          label: 'Plaquette activée',
          position: { x: 180, y: 160 },
        },
        {
          id: 'n-adp',
          type: 'molecule',
          color: 'green',
          label: 'ADP',
          position: { x: 330, y: 165 },
        },
        {
          id: 'n-cox1',
          type: 'process',
          color: 'blue',
          label: 'COX-1',
          position: { x: 410, y: 160 },
        },
        {
          id: 'n-txa2',
          type: 'molecule',
          color: 'blue',
          label: 'TXA₂',
          position: { x: 530, y: 165 },
        },
        {
          id: 'n-gp2b3a',
          type: 'receptor',
          color: 'purple',
          label: 'GPIIb/IIIa',
          position: { x: 620, y: 160 },
        },
        {
          id: 'n-aggreg',
          type: 'process',
          color: 'yellow',
          label: 'Agrégation',
          position: { x: 760, y: 160 },
        },
        {
          id: 'n-thrombus',
          type: 'outcome',
          label: 'Thrombus artériel',
          position: { x: 900, y: 160 },
        },
        // Bottom — Dipyridamole
        {
          id: 'n-dipy',
          type: 'drug',
          color: 'orange',
          label: 'Dipyridamole',
          sublabel: '↑ AMPc plaquettaire',
          position: { x: 340, y: 300 },
        },
      ],
      edges: [
        // Inhibitions (top → main flow)
        { id: 'e-th-p', source: 'n-thieno', target: 'n-p2y12', kind: 'inhibition' },
        { id: 'e-as-c', source: 'n-aspi', target: 'n-cox1', kind: 'inhibition' },
        { id: 'e-ag-g', source: 'n-antigp', target: 'n-gp2b3a', kind: 'inhibition' },
        // Main flow
        { id: 'e-p-pl', source: 'n-p2y12', target: 'n-platelet', kind: 'flow' },
        { id: 'e-pl-adp', source: 'n-platelet', target: 'n-adp', kind: 'flow' },
        { id: 'e-adp-cox', source: 'n-adp', target: 'n-cox1', kind: 'flow' },
        { id: 'e-cox-txa', source: 'n-cox1', target: 'n-txa2', kind: 'flow', label: '↓ TXA₂' },
        { id: 'e-txa-gp', source: 'n-txa2', target: 'n-gp2b3a', kind: 'flow' },
        { id: 'e-gp-ag', source: 'n-gp2b3a', target: 'n-aggreg', kind: 'flow' },
        { id: 'e-ag-th', source: 'n-aggreg', target: 'n-thrombus', kind: 'flow' },
        // Dipyridamole inhibits the cascade upstream of aggregation
        { id: 'e-dipy-pl', source: 'n-dipy', target: 'n-platelet', kind: 'inhibition' },
      ],
    },
  ],
  sources: [],
};

/** Validated at module load so a schema break fails fast in dev. */
export const ANTIPLATELET_SAMPLE_FICHE = Fiche.parse(antiplateletFiche);
